import DOMPurify from "dompurify";
import type { EmailAddress } from "./types";
import { proxyUrl } from "./api";

export function senderName(from: EmailAddress[]): string {
    const first = from[0];
    if (!first) return "Unknown sender";
    return first.name || first.email;
}

export function senderEmail(from: EmailAddress[]): string {
    return from[0]?.email ?? "";
}

export function formatDate(timestamp: number): string {
    const d = new Date(timestamp * 1000);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        ...(sameYear ? {} : { year: "numeric" }),
    });
}

export function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KiB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

/* html↔plain conversion ports from SnappyMail's Common/Html.js (same output the
 * PHP backend has been paired with for years — parity, not approximation). */

const tmpl = document.createElement("template");

/** Convert HTML editor content to plain text (blockquotes, lists, links, md-ish). */
export function htmlToPlain(html: string): string {
    const hr = "⎯".repeat(64);
    const forEach = (selector: string, fn: (node: Element) => void) =>
        tmpl.content.querySelectorAll(selector).forEach((n) => fn(n as Element));

    const blockquotes = (node: Element | DocumentFragment) => {
        let bq: Element | null;
        while ((bq = node.querySelector("blockquote"))) {
            blockquotes(bq);
            bq.replaceWith(
                "\n" + ("\n" + (bq.textContent ?? "").replace(/\n{3,}/g, "\n\n").trim() + "\n").replace(/^/gm, "> ")
            );
        }
    };

    let h = html
        .replace(/<pre[^>]*>([\s\S]*?)<\/pre>/gim, (_m, pre: string) => pre.replace(/\n/g, "<br>"))
        .replace(/<br><\/div>/gi, "</div>")
        .replace(/\r?\n/g, "")
        .replace(/\s+/gm, " ");

    while (/<(div|tr)[\s>]/i.test(h)) h = h.replace(/\n*<(div|tr)(\s[\s\S]*?)?>\n*/gi, "\n");
    while (/<\/(div|tr)[\s>]/i.test(h)) h = h.replace(/\n*<\/(div|tr)(\s[\s\S]*?)?>\n*/gi, "\n");

    tmpl.innerHTML = h
        .replace(/<t[dh](\s[\s\S]*?)?>/gi, "\t")
        .replace(/<\/tr(\s[\s\S]*?)?>/gi, "\n");

    forEach("style", (n) => n.remove());
    forEach("hr", (n) => n.replaceWith(`\n\n${hr}\n\n`));
    forEach("h1,h2,h3,h4,h5,h6", (n) => n.replaceWith(`\n\n${"#".repeat(Number(n.tagName[1]))} ${n.textContent}\n\n`));
    forEach("p", (n) => {
        n.prepend("\n\n");
        if ((n.textContent ?? "").trim() === "") n.remove();
        else n.after("\n\n");
    });
    forEach("ol,ul", (n) => {
        let prefix = "";
        const ordered = n.tagName === "OL";
        let i = 0;
        let parent: Element | null = n;
        while ((parent = parent?.parentElement?.closest?.("ol,ul") ?? null)) prefix = "    " + prefix;
        n.querySelectorAll(":scope > li").forEach((li) => {
            li.prepend("\n" + prefix + (ordered ? `${++i}. ` : " * "));
        });
        n.prepend("\n\n");
        n.after("\n\n");
    });
    forEach("a", (a) => {
        const txt = a.textContent ?? "";
        const href = (a as HTMLAnchorElement).href;
        a.replaceWith(
            txt.replace(/[\s()-]+/g, "").includes(href.replace(/^[a-z]:/, "").replace(/[\s()-]+/g, ""))
                ? txt
                : txt + " " + href + " "
        );
    });
    forEach("b,strong", (b) => b.replaceWith(`**${b.textContent}**`));
    forEach("i,em", (i) => i.replaceWith(`*${i.textContent}*`));

    tmpl.innerHTML = tmpl.innerHTML
        .replace(/\n{3,}/gm, "\n\n")
        .replace(/\n<br[^>]*>/g, "\n")
        .replace(/<br[^>]*>\n/g, "\n");
    forEach("br", (br) => br.replaceWith("\n"));
    blockquotes(tmpl.content);

    return (tmpl.content.textContent || "").trim();
}

const urlRegExp = /((https?|ftp):\/\/[^\s<>"']+)/g;
const emailRegExp = /([^\s<>"']+@[^\s<>"']+\.[^\s<>"']+)/g;

/** Convert plain text to HTML (escaped, linkified, `>` blockquote support). */
export function plainToHtml(plain: string): string {
    plain = plain
        .toString()
        .replace(/\r/g, "")
        .replace(/^>[> ]>+/gm, (m) => m.replace(/[ ]+/g, ""))
        .replace(/\u2028/g, " ");

    let bIn = false;
    let bDo = true;
    let bStart = true;
    let aNextText: string[] = [];
    let aText = plain.split("\n");

    do {
        bDo = false;
        aNextText = [];
        aText.forEach((sLine) => {
            bStart = sLine.slice(0, 1) === ">";
            if (bStart && !bIn) {
                bDo = true;
                bIn = true;
                aNextText.push("~~~blockquote~~~", sLine.slice(1));
            } else if (!bStart && bIn) {
                if (sLine) {
                    bIn = false;
                    aNextText.push("~~~/blockquote~~~", sLine);
                } else {
                    aNextText.push(sLine);
                }
            } else if (bStart && bIn) {
                aNextText.push(sLine.slice(1));
            } else {
                aNextText.push(sLine);
            }
        });
        if (bIn) {
            bIn = false;
            aNextText.push("~~~/blockquote~~~");
        }
        aText = aNextText;
    } while (bDo);

    return aText
        .join("\n")
        .replace(/&/g, "&amp;")
        .replace(/>/g, "&gt;")
        .replace(/</g, "&lt;")
        .replace(urlRegExp, (m) => `<a href="${m}" target="_blank">${m}</a>`)
        .replace(emailRegExp, (_m, e: string) => `<a href="mailto:${e}">${e}</a>`)
        .replace(/~~~blockquote~~~\s*/g, "<blockquote>")
        .replace(/\s*~~~\/blockquote~~~/g, "</blockquote>")
        .replace(/\n/g, "<br>")
        .trim();
}

/** Sanitize message HTML with DOMPurify (all scripts/events/iframes stripped).
 * When `proxyImages` is set, external <img> sources are rewritten through the
 * webmail proxy so the server never leaks the client's address to the sender.
 * (cid: images are left untouched — inline attachment images need the cid→
 * attachment substitution step, out of scope for this stage.) */
export function sanitizeHtml(html: string, proxyImages = true): string {
    const clean = DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ["target"],
        /* default DOMPurify allows data:/cid:/https:… but not blob: — we need
         * blob: for inline cid: images rendered via object URLs */
        ALLOWED_URI_REGEXP:
            /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|matrix|blob):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    });
    if (!proxyImages) return clean;
    return clean.replace(/<img\s([^>]*?)src="(https?:\/\/[^"]+)"/gi, (_full, attrs, src) => {
        return `<img ${attrs}src="${proxyUrl(src)}"`;
    });
}
